import { CountryCode } from 'lib/constants/locales';
import { FieldsMap } from 'lib/fields';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { CombinedStep, Step, StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import { NavbarLogoDisplay } from 'lib/interfaces/ThemeSerialized';
import FlowType from 'verify/lib/interfaces/FlowType';

export enum InquiryStatus {
  Created = 'created',
  Pending = 'pending',
  Completed = 'completed',
  Expired = 'expired',
  Approved = 'approved',
  NeedsReview = 'needs_review',
  Declined = 'declined',
  Failed = 'failed',
}

export interface InquiryTransition {
  'requires-polling': boolean;
}

export enum PollingMode {
  BACKGROUND = 'background',
  BLOCKING = 'blocking',
  NONE = 'none',
}

export enum ReusablePersonaStatus {
  CREATED = 'created',
  ERRORED = 'errored',
  AUTHENTICATED = 'authenticated',
  UPDATED = 'updated',
}

export interface TransitionPollingConfig {
  'interval-ms'?: number;
  'max-attempts'?: number;
  'polling-mode': PollingMode;
  'currently-transitioning-with-persona'?: boolean;
}

export interface RedirectQueryStringConfig {
  /**
   * Controls if inquiry-id is included.
   *
   * Note that additional client-side logic applies even if true:
   * - Only sent if next step is a terminal step.
   * - For WebView, should only be included if event is FromWidgetEvent.Complete.
   */
  'include-inquiry-id': boolean;
  /**
   * Controls if reference-id is included.
   *
   * Note that additional client-side logic applies even if true:
   * - Only sent if next step is a terminal step.
   */
  'include-reference-id': boolean;
  /**
   * Controls if status is included.
   *
   * Note that additional client-side logic applies even if true:
   * - Only sent if next step is a terminal step.
   */
  'include-status': boolean;
  /**
   * Controls if fields is included. `field_keys` is used to determine which
   * fields are included.
   *
   * Note that additional client-side logic applies even if true:
   * - Only sent if next step is a terminal step.
   *
   */
  'include-fields': 'allowlist' | 'blocklist';
  /**
   * if include-fields is 'allowlist' or 'blocklist', this is used to determine which fields are included.
   */
  'field-keys': string[];
}

export interface Logo {
  'content-type': string | null;
  display: NavbarLogoDisplay | null;
  url: string | null;
}

export interface InquiryAttributes {
  'template-id'?: string;
  'completed-at'?: string;
  'created-at'?: string;
  environment?: 'sandbox' | 'production';
  'country-code'?: CountryCode;
  'one-time-link-code'?: string;
  'redirect-uri'?: string;
  /**
   * If undefined, should fall back to client-side generation of query string.
   * If empty object, should omit query string entirely.
   */
  'redirect-query-string-config'?: RedirectQueryStringConfig;
  status?: InquiryStatus;
  'reference-id'?: string;
  'next-step': Step;
  'flow-type'?: FlowType;
  'selected-country-code'?: CountryCode;
  'accepted-policies-at'?: string;
  'wait-for-transition'?: TransitionPollingConfig;
  'reusable-persona-status'?: ReusablePersonaStatus;
  logo: Logo | null;

  // INQUIRY 2.0 ONLY -- these are used for prefill and should not show up for Dynamic Flow.
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-subdivision-abbr'?: string;
  'address-postal-code'?: string;
  'phone-number'?: string;
  'identification-number'?: string;
  'email-address'?: string;

  // Only sent when terminal
  fields?: FieldsMap;
}

export interface InquiryRelationships {
  account: ApiRelationship<ApiModel.Account>;
  template: ApiRelationship<ApiModel.Template>;
  'inquiry-template-version': ApiRelationship<ApiModel.InquiryTemplateVersion>;
  theme: ApiRelationship<ApiModel.Theme>;
  verifications: ApiListRelationship<ApiModel.Verification>;
  environment: ApiRelationship<
    ApiModel.Environment | ApiModel.EnvironmentProduction | ApiModel.EnvironmentSandbox
  >;
  organization: ApiRelationship<ApiModel.Organization>;
}

export type Inquiry = ApiResource<InquiryAttributes, InquiryRelationships, ApiModel.Inquiry>;

export function getPollingMode(inquiry?: Inquiry): PollingMode {
  return inquiry?.attributes['wait-for-transition']?.['polling-mode'] ?? PollingMode.NONE;
}

export function isUiStep(step: Step): step is StepUi {
  switch (step.type) {
    case UiStepType.Ui:
      return true;
    default:
      return false;
  }
}

export function isCombinedStep(step: Step): step is CombinedStep {
  switch (step.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
      return true;
    default:
      return false;
  }
}
